import React, { useCallback, useEffect, useRef, useState } from "react";

const MIN_W = 360, MIN_H = 420, MARGIN = 24;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const load = () => { try { return JSON.parse(localStorage.getItem("nv_ai_panel_size") || "null"); } catch { return null; } };

/** Desktop size state for the AI panel: persisted, clamped to the viewport, resizable from the top-left corner. */
export function useAIPanelSize() {
  const [size, setSize] = useState(() => load() || { w: 420, h: 580 });
  const [fullscreen, setFullscreen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const drag = useRef(null);

  const fit = useCallback((s) => ({
    w: clamp(s.w, MIN_W, Math.max(MIN_W, window.innerWidth - MARGIN * 2)),
    h: clamp(s.h, MIN_H, Math.max(MIN_H, window.innerHeight - MARGIN * 2)),
  }), []);

  useEffect(() => {
    const onResize = () => setSize((s) => fit(s));
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [fit]);

  useEffect(() => { try { localStorage.setItem("nv_ai_panel_size", JSON.stringify(size)); } catch {} }, [size]);

  const onResizeStart = (e) => {
    e.preventDefault();
    drag.current = { x: e.clientX, y: e.clientY, w: size.w, h: size.h };
    const move = (ev) => {
      if (!drag.current) return;
      setSize(fit({ w: drag.current.w + (drag.current.x - ev.clientX), h: drag.current.h + (drag.current.y - ev.clientY) }));
    };
    const up = () => { drag.current = null; window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  return { size, fullscreen, setFullscreen, minimized, setMinimized, onResizeStart };
}
